import currency from "currency.js";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";
import { useCustomerAnalytics } from "../../../../services/hooks/useCustomerAnalytics";
import { useAddToCartTracking } from "../../../../services/hooks/useAddToCartTracking";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import useVisibilityChange from "../../../../services/hooks/useVisibilityChange";
import { useCartMutations } from "../../../../services/hooks/useCartMutations";
import { fetchUpdatedReceipt } from "../../../../services/redux/http/receiptFetch";
import {
  setBottomSheetState,
  setLoading,
} from "../../../../services/redux/slices/globalComponentsToggleSlice";
import { setPreviousOrdersRedux } from "../../../../services/redux/slices/receiptSlice";
import { getPreviousOrders } from "../../../../utils/APICalls/APICalls";
import { buildCategoryAddLineInput } from "../../../../utils/cart/cartLineTypes";
import { getCookie } from "../../../../utils/CookieHelper";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  ListSurface,
  PAYMENT_METHOD,
  ReceiptItemStateEnum,
} from "../../../../utils/enums";
import env from "../../../../utils/env.config";
import { reCheckItemDetails } from "../../../../utils/help/itemDetails/itemDetails";
import { isNumber } from "../../../../utils/help/maths/maths";
import {
  checkIfCurrentFranchiseSlugCorrect,
  getFranchiseSlug,
} from "../../../../utils/help/navigation/navigation";
import { getFranchiseCustomPrices } from "../../../../utils/help/pricing/pricing";
import { isVisitor } from "../../../../utils/help/user/user";
import {
  ItemType,
  PaidReceipt,
  RestaurantTypeEnum,
  SubItemType,
} from "../../../../utils/types";
import { LoadingDotBars } from "../../../global/NewLoading/LoadingDotBars";
import { CurrentOrderV2 } from "./CurrentOrder/CurrentOrderV2/CurrentOrderV2";
import { PreviousOrderV2 } from "./PreviousOrders/PreviousOrderV2/PreviousOrderV2";
import {
  handleAddonGroupsForItem,
  validateAddonRequirements,
} from "./PreviousOrdersHelper";
import styles from "./PreviousOrdersList.module.scss";

export const PreviousOrdersList = (props: { surface?: ListSurface }) => {
  const { surface = ListSurface.home } = props;
  const lodashClonedeep = require("lodash.clonedeep");
  const router = useRouter();
  const dispatch: any = useAppDispatch();
  const { addItem } = useCartMutations();
  const { t } = useTranslationI18();
  const addCustomerAnalytics = useCustomerAnalytics();
  const trackAddToCart = useAddToCartTracking();

  const { franchise } = router.query;

  const { restaurantInfo, selectedFranchise } = useAppSelector(
    (state) => state.config
  );
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );
  const loading = useAppSelector(
    (state) => state.globalComponentsToggleSlice.loading
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  const { previousOrders } = useAppSelector((state) => state.receipt);

  const restaurantType = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );
  const isEcommerce: boolean = restaurantType === RestaurantTypeEnum.ECOMMERCE;
  const isCurrentOrder = (receipt: PaidReceipt) => {
    const threeHoursAgo = new Date();
    threeHoursAgo.setHours(threeHoursAgo.getHours() - 3);
    const thisWeek = new Date();
    thisWeek.setDate(thisWeek.getDate() - 7);
    const notOnline = receipt.paymentMethod !== PAYMENT_METHOD.ONLINE_CARD;
    const onlinePaid =
      receipt.paymentMethod === PAYMENT_METHOD.ONLINE_CARD &&
      receipt.paid_at !== null;
    const cancelledReceipt = receipt.state === ReceiptItemStateEnum.CANCELLED;
    const completeReceipt = receipt.state === ReceiptItemStateEnum.COMPLETED;
    const createdate = new Date(receipt.created_at);

    return (
      (notOnline || onlinePaid) &&
      !cancelledReceipt &&
      !completeReceipt &&
      ((isEcommerce && createdate > thisWeek) || createdate > threeHoursAgo)
    );
  };

  const currentOrders = useMemo(
    () =>
      previousOrders.filter((receipt: PaidReceipt) => isCurrentOrder(receipt)),
    [previousOrders]
  );

  const prevCompleteOrders = useMemo(
    () =>
      previousOrders.filter((receipt: PaidReceipt) => !isCurrentOrder(receipt)),
    [previousOrders]
  );

  function filterValidReceiptItems(receipts: PaidReceipt[]): PaidReceipt[] {
    return receipts.filter((receipt) =>
      receipt.receiptItems.every((receiptItem) => {
        if (
          receiptItem.subItem.allAddonGroups &&
          receiptItem.subItem.allAddonGroups.length > 0
        ) {
          const requiredAddonGroups = receiptItem.subItem.allAddonGroups
            .filter((addonGroup) => (addonGroup.minChoices ?? 0) > 0)
            .map((group) => group.id);

          const selectedAddonGroups = receiptItem.addonList.map(
            (addon) => addon.addonSubItem.addonGroupId
          );

          const missingGroups = requiredAddonGroups.filter(
            (addonGroupId) => !selectedAddonGroups.includes(addonGroupId)
          );

          if (missingGroups.length > 0) {
            return missingGroups.length === 0;
          }

          return validateAddonRequirements(
            receiptItem.subItem.allAddonGroups,
            receiptItem.addonList
          );
        }

        return true;
      })
    );
  }

  const filteredPrevCompleteOrders =
    filterValidReceiptItems(prevCompleteOrders);

  const yourPreviousOrders = t("common:your-previous-orders");
  const noPreviousOrder = t(
    isEcommerce
      ? "common:no-previous-orders-onlineStore"
      : "common:no-previous-orders"
  );

  const hasRoundingMethodAndLevel =
    !!restaurantInfo?.currencyRoundingMethod &&
    !!restaurantInfo?.currencyRoundingLevel;

  useEffect(() => {
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.VIEW_PREVIOUS_ORDERS);

    const token = !!getCookie(COOKIE_ENUM.TOKEN);
    if (
      isVisitor() ||
      !token ||
      !(
        restaurantInfo &&
        checkIfCurrentFranchiseSlugCorrect(
          restaurantInfo,
          getFranchiseSlug(franchise as string)
        )
      )
    ) {
      return;
    }

    fetchMyPreviousOrders(restaurantInfo.id || parseInt(env.restaurantId!));
  }, [restaurantInfo?.id]);

  const fetchMyPreviousOrders = async (restaurantId: number) => {
    try {
      dispatch(setLoading(true));

      const response = await getPreviousOrders(restaurantId);

      dispatch(setPreviousOrdersRedux(response.data));
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const updateAllPreviousOrders = useCallback(async () => {
    for (const receipt of currentOrders) {
      await dispatch(
        fetchUpdatedReceipt({
          receiptId: receipt.id,
          restaurantId: restaurantInfo?.id || parseInt(env.restaurantId!),
        })
      );
    }
  }, [currentOrders.length]);

  useVisibilityChange(updateAllPreviousOrders);

  const reorder = (index: number, receiptItemIndex?: number) => {
    if (
      restaurantInfo &&
      !bottomSheetModal.isOpen &&
      (!selectedFranchise || !orderTypeParam)
    ) {
      dispatch(setBottomSheetState(true));

      return;
    }

    const order = { ...previousOrders[index] };

    if (isNumber(receiptItemIndex)) {
      order.receiptItems = lodashClonedeep([
        { ...order.receiptItems[receiptItemIndex!] },
      ]);
    }
    for (const receiptItem of order.receiptItems) {
      let totalPrice = 0;
      if (!receiptItem.subItem) {
        continue;
      }
      const statusItem = { ...receiptItem.subItem.item };
      statusItem.subItems = lodashClonedeep([{ ...receiptItem.subItem }]);
      const itemStatus = reCheckItemDetails(
        itemStatusRedux,
        statusItem as ItemType,
        true,
        hasRoundingMethodAndLevel
      );
      getFranchiseCustomPrices(
        itemStatus.item.subItems,
        selectedFranchise?.id || -1
      );
      const subItem = {
        ...itemStatus.item.subItems[0],
      } as SubItemType;
      const isOutOfStock = itemStatus.isOutOfStock;
      if (isOutOfStock) {
        continue;
      }

      if (subItem.allAddonGroups && subItem.allAddonGroups.length > 0) {
        const areAddonRequirementsMet = validateAddonRequirements(
          subItem.allAddonGroups,
          receiptItem.addonList
        );

        if (!areAddonRequirementsMet) {
          continue;
        }
      }

      totalPrice += subItem.price;
      let addItemFlag = true;
      if (!subItem.allAddonGroups) {
        continue;
      }
      const addonGroupData = handleAddonGroupsForItem(
        itemStatusRedux,
        subItem,
        hasRoundingMethodAndLevel,
        receiptItem.addonList
      );
      addItemFlag = addItemFlag && addonGroupData.addItemFlag;
      totalPrice += addonGroupData.totalPrice;
      if (!addItemFlag) {
        continue;
      }
      totalPrice = currency(totalPrice).multiply(receiptItem.count).value;
      if (subItem.allAddonGroups) {
        subItem.allAddonGroups.forEach((addonGroup) => {
          addonGroup.addonGroupSubItem.forEach((addonSubItem) => {
            if (
              addonSubItem.subItem.item &&
              !addonSubItem.subItem.item?.subItems
            ) {
              addonSubItem.subItem.item.subItems = [];
            }
          });
        });
      }
      const itemData = buildCategoryAddLineInput(
        receiptItem.note,
        receiptItem.count,
        totalPrice,
        {
          ...JSON.parse(JSON.stringify(subItem)),
          item: undefined,
        },
        subItem.item!
      );
      addItem(itemData);

      // One event per reordered line, each with its own id, matching the
      // analytics rows this loop already wrote. Folding a reorder into a single
      // event would misreport `contents`.
      trackAddToCart({
        subItemId: itemData.subItemDetails.id || itemData.itemDetails.id,
        subItemName:
          itemData.subItemDetails.fullName || itemData.subItemDetails.name,
        quantity: receiptItem.count,
        totalPrice,
        itemId: subItem.item?.id,
      });
    }
  };

  return (
    <>
      <div
        className={`${styles.previous_orders} ${styles[surface]}`}
        data-testid={"previousOrdersPage"}
      >
        {!loading &&
          currentOrders.map((receipt) => (
            <CurrentOrderV2
              key={receipt.id}
              receipt={receipt}
              multipleOrders={currentOrders.length > 1}
            />
          ))}
        <div className={styles.previous_orders__wrapper}>
          {currentOrders.length > 0 && (
            <h2 className={styles.previous_orders__wrapper__title}>
              {yourPreviousOrders}
            </h2>
          )}

          {!loading && filteredPrevCompleteOrders.length > 0 ? (
            filteredPrevCompleteOrders.map((order, index) => (
              <PreviousOrderV2
                key={index}
                order={order}
                index={index}
                reorder={reorder}
              />
            ))
          ) : loading ? (
            <LoadingDotBars />
          ) : (
            <p className={styles.no_previous_orders_text}>{noPreviousOrder}</p>
          )}
        </div>
      </div>
    </>
  );
};
